import { useState } from "react"
import { Formik, Field, Form as FormikForm, ErrorMessage, type FormikHelpers } from "formik"
import * as Yup from "yup"
import styled, { keyframes } from "styled-components"
import {
  PageContainer,
  VisualSection,
  FormSection,
  Title,
  InputGroup,
  Label,
  Input,
  Error,
  SubmitButton,
  Logo,
  GridRow,
  TextArea,
  Logo2,
  SuccessActions,
  PrimaryLinkButton,
  SecondaryLinkButton,
  FormEyebrow,
  FormIntro,
  TrustList,
  DirectContact,
} from "../RequestContactForm/Style"
import SvgLogo from "../../../app/assets/img/logo.svg"
import missionImage from "@assets/img/mission-image.jpg"
import api from "@utils/api.client"
import { trackEvent } from "@components/utils/tracking.client"

/* -------------------------------------------------------------------------
   Styled Components pour l'animation et le select
--------------------------------------------------------------------------- */

// Animation de fade-in (apparition en fondu + translation légère)
const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`

// Wrapper pour chaque étape afin d'appliquer l'animation
const StepWrapper = styled.div`
  animation: ${fadeIn} 0.5s ease-out;
`

const SubjectGrid = styled.div`
  display: grid;
  gap: 0.7rem;
`

const SubjectButton = styled.button`
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 1rem;
  align-items: center;
  min-height: 76px;
  padding: 1rem;
  border: 1px solid #bdcad5;
  border-left: 3px solid #337abd;
  border-radius: 2px;
  background: #fff;
  color: #173953;
  text-align: left;
  font: inherit;
  font-weight: 750;
  cursor: pointer;

  small {
    display: block;
    margin-top: 0.25rem;
    color: #6b7f90;
    font-weight: 500;
  }

  &::after {
    content: "→";
    color: #337abd;
    font-size: 1.25rem;
  }

  &:hover {
    background: #f2f7fb;
    border-color: #6b9fc6;
  }
`

/* -------------------------------------------------------------------------
   Valeurs initiales et validation avec Yup
--------------------------------------------------------------------------- */
const initialValues = {
  inquiryType: "",
  firstName: "",
  lastName: "",
  email: "",
  phone: "",
  message: "",
}

const validationSchema = Yup.object({
  inquiryType: Yup.string().required("Veuillez sélectionner un sujet."),
  firstName: Yup.string().required("Prénom est requis."),
  lastName: Yup.string().required("Nom est requis."),
  email: Yup.string().email("Adresse courriel invalide.").required("Courriel est requis."),
  phone: Yup.string()
    .matches(/^\D*(\d\D*){10}$/, "Numéro de téléphone invalide (10 chiffres requis).")
    .required("Téléphone est requis."),
  message: Yup.string().required("Veuillez entrer votre message."),
})

/* -------------------------------------------------------------------------
   Composant principal : ContactForm
--------------------------------------------------------------------------- */
const ContactForm = () => {
  // Étape 1 : Choix du sujet ; Étape 2 : Formulaire complet
  const [step, setStep] = useState(1)
  const [inquiryType, setInquiryType] = useState("")
  const [successMessage, setSuccessMessage] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState("")

  // Passage de l'étape 1 à l'étape 2 après vérification que l'utilisateur a bien choisi un sujet
  const handleStepOneNext = (value: string) => {
    setInquiryType(value)
    setStep(2)
  }

  // Permet de revenir à l'étape 1 pour modifier le sujet
  const handleBack = () => {
    setStep(1)
  }

  // Gestion de la soumission du formulaire
  const handleSubmit = async (values: typeof initialValues, { resetForm }: FormikHelpers<typeof initialValues>) => {
    if (isSubmitting) return
    setIsSubmitting(true)
    setSuccessMessage(false)
    setSubmitError("")
    try {
      // Remplacez '/contact' par l'endpoint souhaité
      const response = await api.post("/contact", {
        ...values,
        phone: values.phone.replace(/\D/g, ""),
      })
      if (response.status === 200 || response.status === 201) {
        trackEvent("contact_form_submit", { inquiry_type: values.inquiryType })
        setSuccessMessage(true)
        resetForm()
      }
    } catch {
      setSubmitError("Votre message n’a pas pu être envoyé. Réessayez ou appelez-nous directement.")
    } finally {
      setIsSubmitting(false)
    }
  }

  if (successMessage) {
    return (
      <PageContainer>
        <VisualSection src={missionImage} alt="" />
        <Logo href="/" aria-label="Retour à l’accueil">
          <SvgLogo />
        </Logo>
        <FormSection>
          <FormEyebrow>Message reçu</FormEyebrow>
          <Title>Merci. Nous avons bien reçu votre message.</Title>
          <FormIntro>Une personne de l’équipe prendra connaissance du contexte fourni et vous répondra par les coordonnées indiquées.</FormIntro>
          <SuccessActions>
            <PrimaryLinkButton href="/">Retour à l'accueil</PrimaryLinkButton>
            <SecondaryLinkButton href="/guide-proprietaires-portneuf">Découvrir nos ressources</SecondaryLinkButton>
          </SuccessActions>
        </FormSection>
      </PageContainer>
    )
  }

  return (
    <PageContainer>
      <VisualSection src={missionImage} alt="" />
      <Logo href="/" aria-label="Retour à l’accueil">
        <SvgLogo />
      </Logo>
      <FormSection>
        {step === 1 && (
          <StepWrapper>
            <FormEyebrow>Nous joindre</FormEyebrow>
            <Title>Comment pouvons-nous vous aider?</Title>
            <FormIntro>Choisissez le sujet le plus proche. Votre message sera dirigé vers la bonne personne.</FormIntro>
            <SubjectGrid>
              <SubjectButton type="button" onClick={() => handleStepOneNext("gestion-immobiliere")}>
                <span>
                  Parler de gestion immobilière<small>Immeubles locatifs, chalets, services ou disponibilité.</small>
                </span>
              </SubjectButton>
              <SubjectButton type="button" onClick={() => handleStepOneNext("locataire")}>
                <span>
                  Je suis locataire<small>Question générale; pour un bris, utilisez Espace TB si vous avez un accès.</small>
                </span>
              </SubjectButton>
              <SubjectButton type="button" onClick={() => handleStepOneNext("autre")}>
                <span>
                  Autre demande<small>Partenaire, fournisseur ou question administrative.</small>
                </span>
              </SubjectButton>
            </SubjectGrid>
            <DirectContact>
              Urgence locataire? Appelez le <a href="tel:+14186849004">(418) 684-9004</a>.
            </DirectContact>
          </StepWrapper>
        )}

        {step === 2 && (
          <StepWrapper>
            <FormEyebrow>Message direct</FormEyebrow>
            <Title>Écrivez-nous simplement.</Title>
            <FormIntro>Donnez-nous assez de contexte pour vous répondre utilement. Nous ne revendons jamais vos coordonnées.</FormIntro>
            <Formik initialValues={{ ...initialValues, inquiryType }} validationSchema={validationSchema} onSubmit={handleSubmit}>
              {() => (
                <FormikForm>
                  <GridRow columns="1fr 1fr">
                    <InputGroup>
                      <Label htmlFor="firstName">Prénom</Label>
                      <Field as={Input} id="firstName" name="firstName" autoComplete="given-name" />
                      <ErrorMessage name="firstName" component={Error} />
                    </InputGroup>
                    <InputGroup>
                      <Label htmlFor="lastName">Nom</Label>
                      <Field as={Input} id="lastName" name="lastName" autoComplete="family-name" />
                      <ErrorMessage name="lastName" component={Error} />
                    </InputGroup>
                  </GridRow>

                  <GridRow columns="1fr 1fr">
                    <InputGroup>
                      <Label htmlFor="email">Courriel</Label>
                      <Field as={Input} id="email" name="email" type="email" autoComplete="email" />
                      <ErrorMessage name="email" component={Error} />
                    </InputGroup>
                    <InputGroup>
                      <Label htmlFor="phone">Téléphone</Label>
                      <Field as={Input} id="phone" name="phone" type="tel" inputMode="numeric" autoComplete="tel" />
                      <ErrorMessage name="phone" component={Error} />
                    </InputGroup>
                  </GridRow>

                  <InputGroup>
                    <Label htmlFor="message">Message</Label>
                    <Field
                      as={TextArea}
                      id="message"
                      name="message"
                      placeholder="Votre situation, l’adresse concernée et ce que vous souhaitez savoir…"
                    />
                    <ErrorMessage name="message" component={Error} />
                  </InputGroup>

                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      gap: "1rem",
                    }}
                  >
                    <SubmitButton type="button" onClick={handleBack} empty>
                      Retour
                    </SubmitButton>
                    <SubmitButton type="submit" disabled={isSubmitting}>
                      {isSubmitting ? "Envoi en cours..." : "Envoyer"}
                    </SubmitButton>
                  </div>

                  <TrustList>
                    <li>Réponse par une personne de l’équipe</li>
                    <li>Renseignements traités confidentiellement</li>
                    <li>Aucun engagement requis</li>
                  </TrustList>

                  {submitError && <Error role="alert">{submitError}</Error>}

                  <Logo2 href="/">
                    <SvgLogo />
                  </Logo2>
                </FormikForm>
              )}
            </Formik>
          </StepWrapper>
        )}
      </FormSection>
    </PageContainer>
  )
}

export default ContactForm
